import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CalendarDays, CalendarClock, Users, MapPin, RefreshCw,
  Presentation, FileBarChart, Gavel, Flag, Footprints, Building, Coins, ShieldCheck, FileText,
} from 'lucide-react'
import {
  PageHeader, SectionCard, EmptyState, Badge, KpiTile, KpiGrid, Button,
  EventModal, KeyDateModal,
  type BadgeTone,
} from '@/components/ui'
import { useMsal } from '@azure/msal-react'
import { Plus } from 'lucide-react'
import { useStore } from '@/store/useStore'
import { fetchOutlookEvents, type OutlookEvent } from '@/auth/outlookCalendar'
import { signedInAccount } from '@/auth/ssoMemory'
import { date, daysUntil, todayIso} from '@/lib/format'
import type {
  Dataset, EntityRef, ScheduledEvent, KeyDate, ScheduledEventType, KeyDateType, KeyDateStatus,
} from '@/types/entities'
import './Calendar.css'

const TODAY = todayIso()

/* ---- unified agenda entry ---- */
interface AgendaEntry {
  id: string
  kind: 'event' | 'keyDate' | 'outlook'
  title: string
  /** date portion 'YYYY-MM-DD' for grouping + days-until */
  day: string
  /** raw datetime for time display (events only) */
  at?: string
  typeLabel: string
  tone: BadgeTone
  icon: React.ReactNode
  ref?: EntityRef
  attendees?: string[]
  location?: string
  relatedPackage?: string
  owner?: string
  webLink?: string
}

/* ---- event styling ---- */
const EVENT_ICON: Record<ScheduledEventType, React.ReactNode> = {
  'Performance Review': <Presentation size={15} />,
  'Sales Meeting': <Users size={15} />,
  'Reporting Window': <FileBarChart size={15} />,
  Hearing: <Gavel size={15} />,
  Deadline: <Flag size={15} />,
  'Site Visit': <Footprints size={15} />,
  Internal: <Users size={15} />,
}
const EVENT_TONE: Record<ScheduledEventType, BadgeTone> = {
  'Performance Review': 'navy',
  'Sales Meeting': 'info',
  'Reporting Window': 'info',
  Hearing: 'alert',
  Deadline: 'warn',
  'Site Visit': 'ok',
  Internal: 'neutral',
}

/* ---- key-date styling ---- */
const KD_ICON: Record<KeyDateType, React.ReactNode> = {
  Refi: <Coins size={15} />,
  'Loan Maturity': <Coins size={15} />,
  Valuation: <Building size={15} />,
  Covenant: <ShieldCheck size={15} />,
  'Reporting Window': <FileBarChart size={15} />,
  Stabilization: <Building size={15} />,
  'Contract Renewal': <FileText size={15} />,
  Budget: <FileBarChart size={15} />,
  Insurance: <ShieldCheck size={15} />,
  'Hard Money': <Coins size={15} />,
  'DD Expiration': <Flag size={15} />,
  Extension: <Flag size={15} />,
  Hearing: <Gavel size={15} />,
  'Permit Expiration': <Flag size={15} />,
}
const KD_TONE: Record<KeyDateStatus, BadgeTone> = {
  Overdue: 'alert', 'Due Soon': 'warn', Upcoming: 'info', Met: 'ok', Waived: 'neutral',
}

/** Resolve a ref to a route; tracks roll up to their account. */
function routeForRef(d: Dataset, ref?: EntityRef): string | undefined {
  if (!ref) return undefined
  switch (ref.kind) {
    case 'account': return `/accounts/${ref.id}`
    case 'property': return `/properties/${ref.id}`
    case 'contact': return `/contacts/${ref.id}`
    case 'opportunity': return `/opportunities/${ref.id}`
    case 'fund': return `/funds/${ref.id}`
    case 'track': {
      const t = d.tracks.find((x) => x.id === ref.id)
      return t ? `/accounts/${t.accountId}` : undefined
    }
    default: return undefined
  }
}

function labelForRef(d: Dataset, ref?: EntityRef): string | undefined {
  if (!ref) return undefined
  if (ref.label) return ref.label
  switch (ref.kind) {
    case 'account': return d.accounts.find((a) => a.id === ref.id)?.name
    case 'property': return d.properties.find((p) => p.id === ref.id)?.name
    case 'contact': {
      const c = d.contacts.find((x) => x.id === ref.id)
      return c ? `${c.firstName} ${c.lastName}`.trim() : undefined
    }
    case 'opportunity': return d.opportunities.find((o) => o.id === ref.id)?.name
    case 'track': return d.tracks.find((t) => t.id === ref.id)?.name
    case 'fund': return d.funds.find((f) => f.id === ref.id)?.name
    default: return ref.id
  }
}

/** "h:mm AM/PM" from an ISO datetime (local). */
function timeLabel(at?: string): string | undefined {
  if (!at) return undefined
  const dt = new Date(at)
  if (Number.isNaN(dt.getTime())) return undefined
  return dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function monthKey(day: string): string {
  const dt = new Date(`${day}T00:00:00`)
  return dt.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'event', label: 'Meetings' },
  { id: 'keyDate', label: 'Critical Dates' },
]

export default function Calendar() {
  const data = useStore((s) => s.data)
  const { instance, accounts: msalAccounts } = useMsal()
  const [filter, setFilter] = useState<'all' | 'event' | 'keyDate'>('all')
  const [outlook, setOutlook] = useState<OutlookEvent[] | null>(null)
  const [outlookStatus, setOutlookStatus] = useState<'loading' | 'ok' | 'error'>('loading')
  const [newEvent, setNewEvent] = useState(false)
  const [newKeyDate, setNewKeyDate] = useState(false)

  // The REAL signed-in user's Outlook calendar (Graph calendarView). The
  // prototype claimed this sync and showed seed data instead.
  useEffect(() => {
    // The ACTIVE account's calendar, never simply the first cached (auth/ssoMemory.ts).
    const account = signedInAccount(instance, msalAccounts)
    if (!account) return
    let cancelled = false
    fetchOutlookEvents(instance, account, 90)
      .then((evs) => { if (!cancelled) { setOutlook(evs); setOutlookStatus('ok') } })
      .catch((err) => {
        console.error('[Calendar] Outlook fetch failed:', err)
        if (!cancelled) setOutlookStatus('error')
      })
    return () => { cancelled = true }
  }, [instance, msalAccounts])

  /* Merge events + key dates into one chronological stream, today forward. */
  const entries = useMemo<AgendaEntry[]>(() => {
    const evs: AgendaEntry[] = data.events.map((e: ScheduledEvent) => ({
      id: e.id,
      kind: 'event',
      title: e.title,
      day: e.start.slice(0, 10),
      at: e.start,
      typeLabel: e.type,
      tone: EVENT_TONE[e.type] ?? 'neutral',
      icon: EVENT_ICON[e.type] ?? <CalendarDays size={15} />,
      ref: e.attachedTo,
      attendees: e.attendees,
      location: e.location,
      relatedPackage: e.relatedPackage,
    }))
    const kds: AgendaEntry[] = data.keyDates.map((k: KeyDate) => ({
      id: k.id,
      kind: 'keyDate',
      title: k.title,
      day: k.date,
      typeLabel: k.type,
      tone: KD_TONE[k.status] ?? 'warn',
      icon: KD_ICON[k.type] ?? <CalendarClock size={15} />,
      ref: k.attachedTo,
      owner: k.owner,
    }))
    const ols: AgendaEntry[] = (outlook ?? []).map((o) => ({
      id: `ol-${o.id}`,
      kind: 'outlook',
      title: o.subject,
      day: o.startIso.slice(0, 10),
      at: o.isAllDay ? undefined : o.startIso,
      typeLabel: 'Outlook',
      tone: 'info',
      icon: <CalendarDays size={15} />,
      location: o.location,
      owner: o.organizer,
      webLink: o.webLink,
    }))
    return [...evs, ...kds, ...ols]
      .filter((x) => x.day >= TODAY)
      .sort((a, b) => {
        if (a.day !== b.day) return a.day < b.day ? -1 : 1
        // same day: timed events before all-day key dates
        const at = a.at ?? `${a.day}T23:59:59`
        const bt = b.at ?? `${b.day}T23:59:59`
        return at < bt ? -1 : 1
      })
  }, [data, outlook])

  const filtered = useMemo(
    () => (filter === 'all' ? entries
      : filter === 'event' ? entries.filter((e) => e.kind === 'event' || e.kind === 'outlook')
      : entries.filter((e) => e.kind === filter)),
    [entries, filter],
  )

  /* Stat strip */
  const stats = useMemo(() => {
    const within30 = entries.filter((e) => {
      const du = daysUntil(e.day); return du != null && du <= 30
    }).length
    const meetings = entries.filter((e) => e.kind === 'event' || e.kind === 'outlook').length
    const critical = entries.filter((e) => e.kind === 'keyDate').length
    const next = entries[0]
    return { total: entries.length, within30, meetings, critical, next }
  }, [entries])

  /* Group filtered entries by month for section headers. */
  const grouped = useMemo(() => {
    const map = new Map<string, AgendaEntry[]>()
    for (const e of filtered) {
      const key = monthKey(e.day)
      const arr = map.get(key) ?? []
      arr.push(e)
      map.set(key, arr)
    }
    return [...map.entries()]
  }, [filtered])

  return (
    <div className="cal">
      <PageHeader
        eyebrow="Schedule"
        title={<>My <b>Calendar</b></>}
        sub={
          stats.next
            ? `${stats.total} upcoming items — next up: ${stats.next.title} (${date(stats.next.day, 'med')}).`
            : 'No upcoming items on the calendar.'
        }
        actions={
          <div className="cal-actions">
            <span className={`cal-sync cal-sync--${outlookStatus}`}>
              <RefreshCw size={13} />
              {outlookStatus === 'ok'
                ? `Outlook connected · ${outlook?.length ?? 0} events`
                : outlookStatus === 'loading' ? 'Connecting to Outlook…'
                : 'Outlook calendar unavailable'}
            </span>
            <Button variant="default" size="sm" icon={<Plus size={14} />} onClick={() => setNewKeyDate(true)}>Critical date</Button>
            <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setNewEvent(true)}>New meeting</Button>
          </div>
        }
      />

      <KpiGrid cols={4}>
        <KpiTile label="Upcoming Items" value={stats.total} sub="today forward" />
        <KpiTile label="Next 30 Days" value={stats.within30} accent={stats.within30 > 0} sub="reviews & deadlines" />
        <KpiTile label="Meetings" value={stats.meetings} sub="scheduled events" />
        <KpiTile label="Critical Dates" value={stats.critical} sub="reporting, valuations, maturities" />
      </KpiGrid>

      <SectionCard
        eyebrow="Agenda"
        title={<>Upcoming <b>Schedule</b></>}
        right={
          <div className="cal-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                className={`cal-filter${filter === f.id ? ' active' : ''}`}
                onClick={() => setFilter(f.id as typeof filter)}
              >
                {f.label}
              </button>
            ))}
          </div>
        }
      >
        {grouped.length === 0 ? (
          <EmptyState
            icon={<CalendarDays size={34} />}
            title="Nothing scheduled"
            hint="Your Outlook calendar plus CRM reviews, reporting windows, site visits, and critical dates appear here."
          />
        ) : (
          <div className="cal-agenda">
            {grouped.map(([month, items]) => (
              <div className="cal-month" key={month}>
                <div className="cal-month-head">
                  <span className="cal-month-name">{month}</span>
                  <span className="cal-month-count">{items.length}</span>
                </div>
                <div className="cal-rows">
                  {items.map((e) => (
                    <AgendaRow key={`${e.kind}-${e.id}`} entry={e} data={data} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="cal-caption">
          <RefreshCw size={11} /> Outlook items are read live from your Microsoft 365 calendar; CRM meetings and critical dates are managed here.
        </div>
      </SectionCard>

      <EventModal open={newEvent} onClose={() => setNewEvent(false)} />
      <KeyDateModal open={newKeyDate} onClose={() => setNewKeyDate(false)} />
    </div>
  )
}

/* ---- one agenda row ---- */
function AgendaRow({ entry, data }: { entry: AgendaEntry; data: Dataset }) {
  const du = daysUntil(entry.day)
  const overdue = du != null && du < 0
  const soon = du != null && du >= 0 && du <= 7
  const to = routeForRef(data, entry.ref)
  const refLabel = labelForRef(data, entry.ref)
  const time = timeLabel(entry.at)
  const dt = new Date(`${entry.day}T00:00:00`)

  return (
    <div className="cal-row">
      <div className="cal-date">
        <span className="cal-date-dow">{dt.toLocaleDateString('en-US', { weekday: 'short' })}</span>
        <span className="cal-date-day num">{dt.getDate()}</span>
        <span className="cal-date-mo">{dt.toLocaleDateString('en-US', { month: 'short' })}</span>
      </div>

      <div className={`cal-rail cal-rail--${entry.tone}`} />

      <div className="cal-main">
        <div className="cal-row-head">
          <span className="cal-icon">{entry.icon}</span>
          {entry.webLink
            ? <a className="cal-title cal-title--link" href={entry.webLink} target="_blank" rel="noreferrer">{entry.title}</a>
            : <span className="cal-title">{entry.title}</span>}
          <Badge tone={entry.tone} variant="soft">{entry.typeLabel}</Badge>
          {entry.kind === 'keyDate' && <Badge tone="neutral" variant="outline">Critical Date</Badge>}
        </div>
        <div className="cal-meta">
          {time && <span className="cal-meta-item"><CalendarClock size={12} />{time}</span>}
          {refLabel && (
            to ? (
              <Link to={to} className="cal-ref-link">{refLabel}</Link>
            ) : (
              <span className="cal-meta-item">{refLabel}</span>
            )
          )}
          {entry.location && <span className="cal-meta-item"><MapPin size={12} />{entry.location}</span>}
          {entry.owner && <span className="cal-meta-item">Owner · {entry.owner}</span>}
          {entry.relatedPackage && <span className="cal-meta-item cal-meta-pkg">{entry.relatedPackage}</span>}
        </div>
        {entry.attendees && entry.attendees.length > 0 && (
          <div className="cal-attendees">
            <Users size={12} />
            <span className="truncate">{entry.attendees.join(', ')}</span>
          </div>
        )}
      </div>

      <div className="cal-when">
        <span className={`cal-when-days num${overdue ? ' cal-when-days--late' : soon ? ' cal-when-days--soon' : ''}`}>
          {du == null ? '—' : overdue ? `${-du}d` : du === 0 ? 'Today' : `${du}d`}
        </span>
        {du != null && du !== 0 && <span className="cal-when-unit">{overdue ? 'ago' : 'away'}</span>}
      </div>
    </div>
  )
}
